import type { LicenceCatalogSection, LicenceSection } from "@xop-any-time/shared";
import { LICENCE_SECTION_LABELS } from "@xop-any-time/shared";
import { Box, ButtonBase, InputAdornment, TextField, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { CheckCircle2, Puzzle, Rocket, Search, XCircle, type LucideIcon } from "lucide-react";
import { useMemo, useState, type JSX } from "react";

/**
 * The licence builder's feature picker (CEO, 2026-09-15).
 *
 * The LOOK is the customer portal's Features page
 * (`xop-edge-customer/apps/portal-web/src/pages/portal/Features.tsx`), so a
 * reseller building a licence sees the same tiles its customer later reads: a
 * segmented All / Selected / Not selected filter, a name search, then one card
 * per section with a tinted icon, a count pill and a grid of tiles. That page
 * is Tailwind; this is the same design in MUI `sx` off the theme palette.
 *
 * THE ROWS ARE PRICING FEATURES (2026-09-15, later), not the appliance's raw
 * keys: what XOP sells in the two licence categories, each naming the
 * appliance key it switches on. That gives a tile three states:
 *  - **linked** (`licenceKey` set) — a normal tile; toggling it toggles the key;
 *  - **unlinked** (`licenceKey` null) — XOP has not yet said which appliance
 *    feature this row licenses, so it is shown greyed and cannot be selected
 *    (`aria-disabled`), with a caption saying so;
 *  - **legacy** — a key already ON the licence that no row maps (set before
 *    the price list learned it, or unlinked since): an extra greyed, SELECTED
 *    tile under Platform Extensions that can only be switched OFF.
 * Nothing prints a code or an appliance key as a tile's name; the legacy
 * caption names the key because it is the only identity such a key has.
 *
 * What differs from the customer page is that a tile here is a CONTROL. Each
 * is a native button with `role="checkbox"`, so Space and Enter toggle it,
 * `disabled` locks it, and its accessible name is the feature's NAME.
 *
 * The grouping stays `licenceCatalogSections` (shared): the caller passes the
 * sections already in the appliance's fixed order, and this component only
 * FILTERS within them. A section with nothing to show under the current filter
 * is hidden; nothing matching at all says so in a sentence.
 */

export type LicenceFeatureFilter = "all" | "selected" | "unselected";

/** A key on the licence that no catalogue row links, with the best name the
 * caller has for it. */
export interface LegacyLicenceFeature {
  key: string;
  name: string;
}

interface Props {
  sections: LicenceCatalogSection[];
  selectedKeys: readonly string[];
  onToggle: (key: string) => void;
  disabled?: boolean;
  legacy?: readonly LegacyLicenceFeature[];
}

type TileState = "linked" | "unlinked" | "legacy";

interface Tile {
  /** Stable React key — a row id or a legacy key; never collides. */
  id: string;
  name: string;
  /** The appliance key toggled; null for an unlinked row. */
  key: string | null;
  state: TileState;
  checked: boolean;
}

interface TileSection {
  section: LicenceSection;
  label: string;
  tiles: Tile[];
}

type PaletteTone = "primary" | "info";

const SECTION_LOOK: Record<LicenceSection, { icon: LucideIcon; tone: PaletteTone }> = {
  value_added_services: { icon: Rocket, tone: "primary" },
  platform_extensions: { icon: Puzzle, tone: "info" },
};

export const UNLINKED_CAPTION = "Not linked to an appliance feature yet — XOP sets that in its price list.";

export function LicenceFeaturePicker({
  sections,
  selectedKeys,
  onToggle,
  disabled = false,
  legacy = [],
}: Props): JSX.Element {
  const [filter, setFilter] = useState<LicenceFeatureFilter>("all");
  const [search, setSearch] = useState("");

  const selected = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const tileSections = useMemo<TileSection[]>(() => {
    const built: TileSection[] = sections.map((section) => ({
      section: section.section,
      label: section.label,
      tiles: section.features.map((feature) => ({
        id: `feature-${feature.id}`,
        name: feature.name,
        key: feature.licenceKey,
        state: feature.licenceKey ? "linked" : "unlinked",
        checked: feature.licenceKey !== null && selected.has(feature.licenceKey),
      })),
    }));
    if (legacy.length > 0) {
      const legacyTiles: Tile[] = legacy.map((feature) => ({
        id: `legacy-${feature.key}`,
        name: feature.name,
        key: feature.key,
        state: "legacy",
        checked: true,
      }));
      const extensions = built.find((section) => section.section === "platform_extensions");
      if (extensions) extensions.tiles.push(...legacyTiles);
      else {
        built.push({
          section: "platform_extensions",
          label: LICENCE_SECTION_LABELS.platform_extensions,
          tiles: legacyTiles,
        });
      }
    }
    return built;
  }, [sections, selected, legacy]);

  const all = useMemo(() => tileSections.flatMap((section) => section.tiles), [tileSections]);
  const selectedCount = all.filter((tile) => tile.checked).length;

  const needle = search.trim().toLowerCase();
  const matches = (tile: Tile): boolean => {
    if (filter === "selected" && !tile.checked) return false;
    if (filter === "unselected" && tile.checked) return false;
    return needle === "" || tile.name.toLowerCase().includes(needle);
  };

  const visible = tileSections
    .map((section) => ({ ...section, tiles: section.tiles.filter(matches) }))
    .filter((section) => section.tiles.length > 0);

  const filters: Array<{ value: LicenceFeatureFilter; label: string }> = [
    { value: "all", label: `All (${all.length})` },
    { value: "selected", label: `Selected (${selectedCount})` },
    { value: "unselected", label: `Not selected (${all.length - selectedCount})` },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <Box
          role="group"
          aria-label="Show features"
          sx={{ display: "inline-flex", bgcolor: "surfaceSoft", borderRadius: 1, p: 0.5, gap: 0.25 }}
        >
          {filters.map((option) => {
            const active = filter === option.value;
            return (
              <ButtonBase
                key={option.value}
                aria-pressed={active}
                onClick={() => setFilter(option.value)}
                sx={{
                  px: 1.75,
                  py: 0.75,
                  borderRadius: 0.75,
                  fontSize: 12.5,
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: active ? "primary.contrastText" : "text.secondary",
                  bgcolor: active ? "primary.main" : "transparent",
                  boxShadow: active ? 1 : 0,
                  transition: "background-color 120ms, color 120ms",
                  "&:hover": { color: active ? "primary.contrastText" : "text.primary" },
                }}
              >
                {option.label}
              </ButtonBase>
            );
          })}
        </Box>
        <TextField
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            // The builder is one form; Enter while searching must not create the licence.
            if (event.key === "Enter") event.preventDefault();
          }}
          placeholder="Search features by name…"
          inputProps={{ "aria-label": "Search features" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} aria-hidden="true" />
              </InputAdornment>
            ),
          }}
          sx={{ ml: { sm: "auto" }, flex: 1, minWidth: 220, maxWidth: { sm: 420 } }}
        />
      </Box>

      {visible.map((section) => (
        <FeatureSection key={section.section} section={section} onToggle={onToggle} disabled={disabled} />
      ))}

      {visible.length === 0 && (
        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 3 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            No features match the current filter.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function FeatureSection({
  section,
  onToggle,
  disabled,
}: {
  section: TileSection;
  onToggle: (key: string) => void;
  disabled: boolean;
}): JSX.Element {
  const { icon: Icon, tone } = SECTION_LOOK[section.section];
  const count = section.tiles.length;
  const headingId = `licence-section-${section.section}`;

  return (
    <Box
      component="section"
      aria-labelledby={headingId}
      data-testid={`licence-section-${section.section}`}
      sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: { xs: 2, lg: 2.5 } }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            display: "grid",
            placeItems: "center",
            color: `${tone}.main`,
            bgcolor: (theme: Theme) => alpha(theme.palette[tone].main, 0.12),
          }}
        >
          <Icon size={16} aria-hidden="true" />
        </Box>
        <Typography component="h3" id={headingId} sx={{ fontSize: 16, fontWeight: 600 }}>
          {section.label}
        </Typography>
        <Box
          component="span"
          aria-label={`${count} ${count === 1 ? "feature" : "features"}`}
          sx={{
            ml: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 999,
            fontSize: 11.5,
            fontWeight: 600,
            color: "primary.main",
            bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.1),
          }}
        >
          {count}
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
        }}
      >
        {section.tiles.map((tile) => (
          <FeatureTile key={tile.id} tile={tile} onToggle={onToggle} disabled={disabled} />
        ))}
      </Box>
    </Box>
  );
}

function FeatureTile({
  tile,
  onToggle,
  disabled,
}: {
  tile: Tile;
  onToggle: (key: string) => void;
  disabled: boolean;
}): JSX.Element {
  const { checked, state } = tile;
  // An UNLINKED row is not a control at all: nothing it could toggle exists.
  // It keeps the checkbox role so the section's count and a screen reader's
  // list stay honest, and says why through `aria-disabled` + its caption
  // rather than a native `disabled`, which would also hide it from the tab
  // order and its caption from a keyboard reader.
  const inert = state === "unlinked";
  const greyed = inert || state === "legacy";
  const caption =
    state === "unlinked"
      ? UNLINKED_CAPTION
      : state === "legacy"
        ? `Legacy appliance key ${tile.key}`
        : null;
  const captionId = caption ? `${tile.id}-caption` : undefined;

  return (
    // A native <button> carrying the checkbox role: Space and Enter toggle it
    // for free, and `disabled` is a real disabled state rather than an
    // aria-only one a click handler has to remember to honour.
    <ButtonBase
      role="checkbox"
      aria-checked={checked}
      aria-label={tile.name}
      {...(captionId ? { "aria-describedby": captionId } : {})}
      {...(inert ? { "aria-disabled": true } : {})}
      {...(state === "unlinked"
        ? { "data-testid": "licence-feature-unlinked" }
        : state === "legacy"
          ? { "data-testid": "licence-feature-legacy" }
          : {})}
      disabled={disabled}
      onClick={() => {
        if (inert || tile.key === null) return;
        onToggle(tile.key);
      }}
      sx={(theme) => ({
        justifyContent: "flex-start",
        alignItems: "flex-start",
        textAlign: "left",
        gap: 1.25,
        p: 1.75,
        borderRadius: 1,
        border: 1,
        borderColor: checked && !greyed ? alpha(theme.palette.success.main, 0.45) : theme.palette.divider,
        bgcolor: greyed
          ? alpha(theme.palette.text.primary, 0.04)
          : checked
            ? alpha(theme.palette.success.main, 0.06)
            : alpha(theme.palette.text.primary, 0.02),
        opacity: greyed ? 0.7 : 1,
        cursor: inert ? "not-allowed" : "pointer",
        transition: "border-color 120ms, background-color 120ms",
        "&:hover": {
          borderColor: inert
            ? theme.palette.divider
            : checked
              ? alpha(theme.palette.success.main, 0.7)
              : alpha(theme.palette.text.primary, 0.25),
        },
        "&.Mui-focusVisible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
        "&.Mui-disabled": { opacity: checked ? 0.85 : 0.6 },
      })}
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          flexShrink: 0,
          mt: 0.125,
          color: checked && !greyed ? "success.main" : "text.disabled",
        }}
      >
        {checked ? <CheckCircle2 size={20} aria-hidden="true" /> : <XCircle size={20} aria-hidden="true" />}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, gap: 0.25 }}>
        <Typography
          component="span"
          sx={{ fontSize: 13.5, fontWeight: checked ? 600 : 400, color: greyed ? "text.secondary" : "text.primary" }}
        >
          {tile.name}
        </Typography>
        {caption && (
          <Typography id={captionId} component="span" sx={{ fontSize: 11.5, color: "text.secondary" }}>
            {caption}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
}

export default LicenceFeaturePicker;
